/**
 * Purpose:
 * - Define the supported locales for public Pragnu pages.
 *
 * Responsibility:
 * - Keep public locale metadata in one place.
 * - Build locale-aware URLs while keeping route names and slugs stable.
 *
 * Affects:
 * - Public routing, navigation, SEO canonical URLs, and hreflang links.
 */

export const DEFAULT_PUBLIC_LOCALE = "en";

export const PUBLIC_LOCALES = Object.freeze({
  uk: Object.freeze({
    label: "Українська",
    hreflang: "uk",
  }),
  en: Object.freeze({
    label: "English",
    hreflang: "en",
  }),
  pl: Object.freeze({
    label: "Polski",
    hreflang: "pl",
  }),
  ru: Object.freeze({
    label: "Русский",
    hreflang: "ru",
  }),
});

export const PUBLIC_LOCALE_CODES = Object.freeze(
  Object.keys(PUBLIC_LOCALES),
);

export function normalizePublicLocale(value) {
  const locale = String(value || "")
    .trim()
    .split("-")[0]
    .toLowerCase();

  return PUBLIC_LOCALE_CODES.includes(locale) ? locale : "";
}

export function getPublicLocaleFromPath(pathname) {
  const firstSegment = String(pathname || "")
    .split("/")
    .filter(Boolean)[0];

  return normalizePublicLocale(firstSegment);
}

export function resolvePublicLocale(pathname, language) {
  return (
    getPublicLocaleFromPath(pathname) ||
    normalizePublicLocale(language) ||
    DEFAULT_PUBLIC_LOCALE
  );
}

export function publicPath(path = "/", locale = DEFAULT_PUBLIC_LOCALE) {
  const resolvedLocale =
    normalizePublicLocale(locale) || DEFAULT_PUBLIC_LOCALE;

  const rawPath = String(path || "/").trim() || "/";

  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(rawPath)) {
    return rawPath;
  }

  const hashIndex = rawPath.indexOf("#");
  const hash = hashIndex >= 0 ? rawPath.slice(hashIndex) : "";
  const withoutHash =
    hashIndex >= 0 ? rawPath.slice(0, hashIndex) : rawPath;

  const queryIndex = withoutHash.indexOf("?");
  const query =
    queryIndex >= 0 ? withoutHash.slice(queryIndex) : "";
  const pathname =
    queryIndex >= 0
      ? withoutHash.slice(0, queryIndex)
      : withoutHash;

  const segments = pathname.split("/").filter(Boolean);

  if (normalizePublicLocale(segments[0])) {
    segments.shift();
  }

  const stablePath =
    segments.length > 0 ? `/${segments.join("/")}` : "/";

  if (stablePath === "/") {
    return `/${resolvedLocale}/${query}${hash}`;
  }

  return `/${resolvedLocale}${stablePath}${query}${hash}`;
}
